// pages/Home.js
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Hero from '../components/Hero';
import Products from '../components/Product';
import PartnersSlider from '../components/PartnerSlider';
import Contact from '../components/Contact';
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 150);
        return () => clearTimeout(timer);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section id="hero">
        <Hero />
      </section>

      {/* Solutions Overview Grid */}
      <section id="products">
        <Products />
      </section>

      {/* KPI Improvement Section */}
      <section id="kpi-improvements" className="kpi-section">
        <div className="kpi-header">
          <h2>{t('kpi.title')}</h2>
          <p>{t('kpi.subtitle')}</p>
        </div>

        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-val">{t('kpi.card1Val')}</div>
            <div className="kpi-label">{t('kpi.card1Label')}</div>
            <div className="kpi-desc">{t('kpi.card1Desc')}</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-val">{t('kpi.card2Val')}</div>
            <div className="kpi-label">{t('kpi.card2Label')}</div>
            <div className="kpi-desc">{t('kpi.card2Desc')}</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-val">{t('kpi.card3Val')}</div>
            <div className="kpi-label">{t('kpi.card3Label')}</div>
            <div className="kpi-desc">{t('kpi.card3Desc')}</div>
          </div>

          <div className="kpi-card">
            <div className="kpi-val">{t('kpi.card4Val')}</div>
            <div className="kpi-label">{t('kpi.card4Label')}</div>
            <div className="kpi-desc">{t('kpi.card4Desc')}</div>
          </div>
        </div>

        {/* Visual Charts Comparison Mock */}
        <div className="kpi-visuals-wrapper">
          <div className="kpi-chart-card">
            <h3>{t('kpi.charts.distanceTitle')}</h3>
            <div className="bar-chart-mock">
              <div className="chart-row">
                <div className="chart-label">
                  <span>{t('kpi.charts.traditionalRoute')}</span>
                  <span>{t('kpi.charts.traditionalBaseline')}</span>
                </div>
                <div className="bar-container">
                  <div className="bar-fill traditional" style={{ width: '100%' }}>
                    <span className="bar-fill-text">100%</span>
                  </div>
                </div>
              </div>

              <div className="chart-row">
                <div className="chart-label">
                  <span>{t('kpi.charts.conveksHeuristics')}</span>
                  <span>{t('kpi.charts.conveksSaved')}</span>
                </div>
                <div className="bar-container">
                  <div className="bar-fill optimized" style={{ width: '78%' }}>
                    <span className="bar-fill-text">78%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="kpi-chart-card">
            <h3>{t('kpi.charts.slaTitle')}</h3>
            <div className="bar-chart-mock">
              <div className="chart-row">
                <div className="chart-label">
                  <span>{t('kpi.charts.traditionalQueues')}</span>
                  <span>{t('kpi.charts.traditionalMet')}</span>
                </div>
                <div className="bar-container">
                  <div className="bar-fill traditional" style={{ width: '60%' }}>
                    <span className="bar-fill-text">60%</span>
                  </div>
                </div>
              </div>

              <div className="chart-row">
                <div className="chart-label">
                  <span>{t('kpi.charts.conveksPriority')}</span>
                  <span>{t('kpi.charts.conveksSlaBoost')}</span>
                </div>
                <div className="bar-container">
                  <div className="bar-fill optimized" style={{ width: '100%' }}>
                    <span className="bar-fill-text">100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section 1: Digital Twin */}
      <section className="feature-showcase-section">
        <div className="showcase-grid">
          <div className="showcase-text">
            <h2>{t('valueProps.twinTitle')}</h2>
            <p>{t('valueProps.twinText')}</p>
            <ul className="showcase-features-list">
              {Array.isArray(t('valueProps.twinBullets', { returnObjects: true })) ?
                t('valueProps.twinBullets', { returnObjects: true }).map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                )) : null
              }
            </ul>
          </div>
          <div className="showcase-visual">
            <div className="diagram-container">
              {/* Digital Twin SVG Mock */}
              <svg className="diagram-svg" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="10" y="10" width="380" height="280" rx="12" fill="#161616" stroke="#2a2a2a" strokeWidth="2" />
                {/* Simulated Grid layout */}
                <circle cx="100" cy="90" r="16" fill="#4d4dff" fillOpacity="0.2" stroke="#4d4dff" strokeWidth="2" />
                <circle cx="200" cy="150" r="20" fill="#ffffff" fillOpacity="0.1" stroke="#ffffff" strokeWidth="2" />
                <circle cx="300" cy="210" r="16" fill="#4d4dff" fillOpacity="0.2" stroke="#4d4dff" strokeWidth="2" />
                {/* Connecting paths */}
                <path d="M116 90H180M220 150H284" stroke="#808080" strokeWidth="2" strokeDasharray="4 4" />
                <path d="M100 106V210H284" stroke="#4d4dff" strokeWidth="1.5" strokeOpacity="0.5" />
                {/* Technicians pins */}
                <circle cx="140" cy="90" r="5" fill="#ffffff" />
                <circle cx="250" cy="150" r="5" fill="#ffffff" />
                {/* Label overlays */}
                <text x="30" y="270" fill="#808080" fontSize="12" fontFamily="monospace">MAP MONITOR: LIVE FEED</text>
                <circle cx="360" cy="30" r="6" fill="#4d4dff">
                  <animate attributeName="opacity" values="0.3;1;0.3" dur="2s" repeatCount="indefinite" />
                </circle>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section 2: Real-Time Engine */}
      <section className="feature-showcase-section alt">
        <div className="showcase-grid reverse">
          <div className="showcase-text">
            <h2>{t('valueProps.engineTitle')}</h2>
            <p>{t('valueProps.engineText')}</p>
            <ul className="showcase-features-list">
              {Array.isArray(t('valueProps.engineBullets', { returnObjects: true })) ?
                t('valueProps.engineBullets', { returnObjects: true }).map((bullet, idx) => (
                  <li key={idx}>{bullet}</li>
                )) : null
              }
            </ul>
          </div>
          <div className="showcase-visual">
            <div className="diagram-container">
              {/* Optimization Engine Network SVG Mock */}
              <svg className="diagram-svg" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Floating nodes with optimal paths */}
                <g stroke="#ffffff" strokeWidth="1.5">
                  <line x1="80" y1="150" x2="160" y2="80" strokeOpacity="0.2" />
                  <line x1="80" y1="150" x2="160" y2="220" strokeOpacity="0.2" />
                  <line x1="160" y1="80" x2="240" y2="150" />
                  <line x1="160" y1="220" x2="240" y2="150" />
                  <line x1="240" y1="150" x2="320" y2="150" strokeWidth="3" />
                </g>
                <circle cx="80" cy="150" r="12" fill="#111111" stroke="#808080" strokeWidth="2" />
                <circle cx="160" cy="80" r="12" fill="#111111" stroke="#808080" strokeWidth="2" />
                <circle cx="160" cy="220" r="12" fill="#111111" stroke="#808080" strokeWidth="2" />
                <circle cx="240" cy="150" r="16" fill="#111111" stroke="#ffffff" strokeWidth="2" />
                <circle cx="320" cy="150" r="12" fill="#ffffff" stroke="#ffffff" strokeWidth="2" />
                {/* Path directional arrows */}
                <circle cx="200" cy="115" r="4" fill="#ffffff">
                  <animate attributeName="cx" values="160;240" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="cy" values="80;150" dur="3s" repeatCount="indefinite" />
                </circle>
                <text x="30" y="270" fill="#808080" fontSize="12" fontFamily="monospace">SOLVER SOLVED: 24ms</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Trust slider */}
      <section id="partners">
        <PartnersSlider />
      </section>

      {/* Contact Section */}
      <section id="contact-panel">
        <Contact />
      </section>
    </div>
  );
};

export default Home;
